import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "./BaseEditableTable";
import type { EditableCellConfig, FilterConfig } from "./types";
import type { Child } from "@/types";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { childrenCSVColumns } from "@/utils/csvExport";
import type { RowSelectionState, PaginationState, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react/macro";
import { t, msg } from "@lingui/macro";
import { useEnumOptions } from "@/hooks/settings/useEffectiveEnums";
import { useTableConfig } from "@/hooks/settings/useTableConfig";
import { getChildrenColumns } from "@/utils/columnUtils";
import { openRecordColumn } from "@/utils/columnDefinitions";
import { useLabels } from "@/hooks/settings/useLabels";
import { useAuth } from "@clerk/clerk-react";


// Add a helper alias representing the value type of any Child field
type ChildFieldValue = Child[keyof Child];

interface ChildrenTableProps {
  data: Child[];
  onCellUpdate?: (
    rowId: string,
    field: keyof Child,
    newValue: ChildFieldValue
  ) => Promise<void>;
  enableSelection?: boolean;
  // Server-side pagination props
  serverSidePagination?: boolean;
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  isFetching?: boolean;
  /** First page still loading: skeleton rows instead of the empty state. */
  isLoading?: boolean;
  // Server-side filter/sort/search state, owned by the page (URL)
  initialFilters?: ColumnFiltersState;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  searchTerm?: string;
  onSearchChange?: (search: string) => void;
  hasExternalFilters?: boolean;
  /** Every child matching the current search and filters, for "Export all". */
  exportAll?: () => Promise<Child[]>;
  emptyState?: React.ReactNode;
  /** A failed load: shown in the table body, so the toolbar (and the filter that caused it) stays usable. */
  errorState?: React.ReactNode;
  toolbarRight?: React.ReactNode;
}


export function ChildrenTable({
  data,
  onCellUpdate,
  enableSelection = false,
  serverSidePagination = false,
  totalRows,
  pagination,
  onPaginationChange,
  isFetching = false,
  isLoading = false,
  initialFilters,
  onFilterChange,
  sorting,
  onSortingChange,
  searchTerm,
  onSearchChange,
  hasExternalFilters = false,
  exportAll,
  emptyState,
  errorState,
  toolbarRight,
}: ChildrenTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const navigate = useNavigate();
  const { t: _lingui_t, i18n } = useLingui();
  const { orgId } = useAuth();

  // Get custom enum options
  const { options: statusOptions } = useEnumOptions('children', 'status');
  const { options: genderOptions } = useEnumOptions('children', 'gender');

  // Fetch available labels
  const { data: availableLabels = [] } = useLabels(orgId || "", "all");

  // Get table configuration
  const { data: tableConfig } = useTableConfig('children');

  // Define the columns for the children table using the exported function
  const columns = useMemo<EditableCellConfig<Child>[]>(
    () => getChildrenColumns((messageId: string | MessageDescriptor): string => {
      // Widened to `TranslationFunction`'s `string | MessageDescriptor` parameter. A
      // descriptor is passed straight to `i18n._`; the string branch keeps the existing
      // message-id lookup, so runtime behaviour for the real call sites is unchanged.
      if (typeof messageId !== "string") {
        return i18n._(messageId);
      }
      // Use placeholder API to translate message IDs
      const translations: Record<string, string> = {
        "table.children.columns.internalId": i18n._(t({ id: "table.children.columns.internalId" })),
        "table.children.columns.firstName": i18n._(t({ id: "table.children.columns.firstName" })),
        "table.children.columns.lastName": i18n._(t({ id: "table.children.columns.lastName" })),
        "table.children.columns.gender": i18n._(t({ id: "table.children.columns.gender" })),
        "table.children.columns.birthDate": i18n._(t({ id: "table.children.columns.birthDate" })),
        "table.children.columns.status": i18n._(t({ id: "table.children.columns.status" })),
        "table.children.columns.contractSigned": i18n._(t({ id: "table.children.columns.contractSigned" })),
        "common.yes": i18n._(t({ id: "common.yes", message: "Yes" })),
        "common.no": i18n._(t({ id: "common.no", message: "No" })),
        "table.children.columns.notes": i18n._(t({ id: "table.children.columns.notes" })),
        "table.children.columns.specialCare": i18n._(t({ id: "table.children.columns.specialCare" })),
        "table.children.columns.createdAt": i18n._(t({ id: "table.children.columns.createdAt" })),
        "Male": i18n._(t`children.enums.gender.male`),
        "Female": i18n._(t`children.enums.gender.female`),
        "Other": i18n._(t`children.enums.gender.other`),
        "Non-Binary": i18n._(t`children.enums.gender.non_binary`),
      };
      return translations[messageId] || messageId;
    }, navigate, {
      status: statusOptions,
      gender: genderOptions,
    }, { availableLabels }).concat(openRecordColumn<Child>("/children")),
    [i18n, navigate, statusOptions, genderOptions, availableLabels]
  );

  // Create localized CSV columns based on the imported configuration
  const localizedChildrenCSVColumns = useMemo(() => {
    return childrenCSVColumns.map(column => ({
      ...column,
      header: (() => {
        // Map column keys to translations
        switch (column.key) {
          case 'childInternalId': return i18n._(t({ id: "table.children.columns.internalId" }));
          case 'firstname': return i18n._(t({ id: "table.children.columns.firstName" }));
          case 'lastname': return i18n._(t({ id: "table.children.columns.lastName" }));
          case 'birthdate': return i18n._(t({ id: "table.children.columns.birthDate" }));
          case 'gender': return i18n._(t({ id: "table.children.columns.gender" }));
          case 'status': return i18n._(t({ id: "table.children.columns.status" }));
          case 'contractSigned': return i18n._(t({ id: "table.children.columns.contractSigned" }));
          case 'healthInsurance': return i18n._(t({ id: "table.children.columns.healthInsurance" }));
          case 'paediatrician': return i18n._(t({ id: "table.children.columns.pediatrician" }));
          case 'vaccinated': return i18n._(t({ id: "table.children.columns.vaccinated" }));
          case 'useDiapers': return i18n._(t({ id: "table.children.columns.useDiapers" }));
          case 'allergies': return i18n._(t({ id: "table.children.columns.allergies" }));
          case 'diseases': return i18n._(t({ id: "table.children.columns.diseases" }));
          case 'medications': return i18n._(t({ id: "table.children.columns.medications" }));
          case 'specialCare': return i18n._(t({ id: "table.children.columns.specialCare" }));
          case 'languagesAtHome': return i18n._(t({ id: "table.children.columns.languagesAtHome" }));
          case 'languagesAtDaycare': return i18n._(t({ id: "table.children.columns.languagesAtDaycare" }));
          case 'languageRequest': return i18n._(t({ id: "table.children.columns.languageRequest" }));
          case 'nationalities': return i18n._(t({ id: "table.children.columns.nationalities" }));
          case 'eatingRequest': return i18n._(t({ id: "table.children.columns.eatingRequest" }));
          case 'sleepRequest': return i18n._(t({ id: "table.children.columns.sleepRequest" }));
          case 'toiletRequest': return i18n._(t({ id: "table.children.columns.toiletRequest" }));
          case 'miscRequest': return i18n._(t({ id: "table.children.columns.miscRequest" }));
          case 'selectedDiets': return i18n._(t({ id: "table.children.columns.selectedDiets" }));
          case 'selectedSleepHabits': return i18n._(t({ id: "table.children.columns.sleepHabits" }));
          case 'selectedToiletHabits': return i18n._(t({ id: "table.children.columns.toiletHabits" }));
          case 'labels': return i18n._(t({ id: "table.children.columns.labels" }));
          case 'notes': return i18n._(t({ id: "table.children.columns.notes" }));
          case 'createdAt': return i18n._(t({ id: "table.children.columns.createdAt" }));
          case 'updatedAt': return i18n._(t({ id: "table.children.columns.updatedAt" }));
          default: return column.header || column.key;
        }
      })()
    }));
  }, [i18n]);

  // Define filter configuration for each column (text filtering is handled by global search)
  // Only what the list endpoint filters by; text fields go through the search.
  const filterConfig: FilterConfig<Child> = useMemo(() => ({
    status: 'select',
    labels: 'select',
  }), []);

  // Export lives in the table toolbar; the selection bar only clears.
  const renderSelectionActions = useCallback(
    (_selectedRowIds: string[], clearSelection: () => void) => (
      <Button variant="ghost" size="sm" onClick={clearSelection}>
        {i18n._(t({ id: "table.children.actions.clearSelection" }))}
      </Button>
    ),
    [i18n]
  );

  // Default cell update handler if none provided
  const defaultCellUpdateHandler = async (
    rowId: string,
    field: keyof Child,
    _: ChildFieldValue
  ): Promise<void> => {
    // This is a stub that would be replaced with actual API call
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 500));

    // For production, replace with actual API call
    // Development/demo error simulation can be controlled via environment variable
    if (import.meta.env.DEV && Math.random() > 0.9) {
      throw new Error("Simulated API error");
    }

    toast.success(
      i18n._(msg({ id: "table.children.actions.fieldUpdated" }), { field: String(field), id: rowId })
    );
  };

  // Prepare column visibility and order from table config
  const defaultColumnVisibility = useMemo(() => {
    if (!tableConfig?.visibleColumns) return undefined;

    // Captured after the guard: the narrowing is lost inside the callback below.
    const visibleColumns = tableConfig.visibleColumns;

    // Create visibility state object from visible_columns array
    const visibility: Record<string, boolean> = {};
    
    // First, hide all columns except those that should be visible
    columns.forEach(col => {
      visibility[col.id || col.accessorKey as string] = visibleColumns.includes(col.accessorKey as string);
    });
    visibility.open = true;

    return visibility;
  }, [tableConfig?.visibleColumns, columns]);

  const defaultColumnOrder = useMemo(() => {
    return tableConfig?.columnOrder || undefined;
  }, [tableConfig?.columnOrder]);

  return (
    <BaseEditableTable<Child>
      data={data}
      columns={columns}
      onCellUpdate={onCellUpdate || defaultCellUpdateHandler}
      defaultSortField={(tableConfig?.defaultSortColumn as keyof Child) || "firstname"}
      defaultSortOrder={tableConfig?.defaultSortDesc ? 'desc' : 'asc'}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      enableSearch={true}
      searchPlaceholder={i18n._(t({ id: "table.children.search.placeholder" }))}
      searchTerm={searchTerm}
      onSearchChange={onSearchChange}
      enableFilters={true}
      filterConfig={filterConfig}
      onFilterChange={onFilterChange}
      hasExternalFilters={hasExternalFilters}
      sorting={sorting}
      onSortingChange={onSortingChange}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="children-table"
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      defaultColumnFilters={initialFilters}
      // Server-side pagination props
      serverSidePagination={serverSidePagination}
      totalRows={totalRows}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      isFetching={isFetching}
      isLoading={isLoading}
      enableExport={true}
      exportAll={exportAll}
      exportColumns={localizedChildrenCSVColumns}
      exportFileName="children"
      emptyState={errorState ?? emptyState}
      filteredEmptyState={errorState}
      toolbarRight={toolbarRight}
    />
  );
}